/**
 * Auth retry utilities with exponential backoff.
 *
 * Provides retry logic for token refresh operations to handle
 * transient network failures during authentication.
 */

import { logger } from "./logger";

/** Configuration for auth retry */
interface AuthRetryConfig {
  maxAttempts: number;
  baseDelayMs: number;
  maxDelayMs: number;
}

/** Default retry configuration for auth operations */
const DEFAULT_AUTH_RETRY_CONFIG: AuthRetryConfig = {
  maxAttempts: 3,
  baseDelayMs: 1000,
  maxDelayMs: 8000,
};

/**
 * Calculate delay with exponential backoff and jitter.
 *
 * @param attempt - Current attempt number (0-indexed)
 * @param config - Retry configuration
 * @returns Delay in milliseconds
 */
function getRetryDelay(attempt: number, config: AuthRetryConfig): number {
  const baseDelay = Math.min(config.baseDelayMs * 2 ** attempt, config.maxDelayMs);
  // Add jitter: 50%-100% of base delay to spread out retries
  return Math.floor(baseDelay * (0.5 + Math.random() * 0.5));
}

/**
 * Sleep for specified milliseconds.
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Execute an async operation with retry and exponential backoff.
 *
 * @param operation - The async operation to execute
 * @param operationName - Name for logging purposes
 * @param config - Retry configuration
 * @returns Promise with the operation result
 * @throws The last error if all retries fail
 */
export async function withAuthRetry<T>(
  operation: () => Promise<T>,
  operationName: string,
  config: AuthRetryConfig = DEFAULT_AUTH_RETRY_CONFIG,
): Promise<T> {
  let lastError: Error | undefined;

  for (let attempt = 0; attempt < config.maxAttempts; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      // Check if error is retryable (network/server errors, not auth failures)
      const isRetryable = isRetryableError(lastError);

      if (!isRetryable) {
        logger.warn("auth_retry_not_retryable", {
          operation: operationName,
          error: lastError.message,
        });
        throw lastError;
      }

      if (attempt < config.maxAttempts - 1) {
        const delay = getRetryDelay(attempt, config);
        logger.info("auth_retry_attempt", {
          operation: operationName,
          attempt: attempt + 1,
          maxAttempts: config.maxAttempts,
          delayMs: delay,
          error: lastError.message,
        });
        await sleep(delay);
      }
    }
  }

  logger.error("auth_retry_exhausted", {
    operation: operationName,
    attempts: config.maxAttempts,
    error: lastError?.message,
  });
  throw lastError;
}

/**
 * Check if an error is retryable (network/transient vs auth failure).
 *
 * @param error - The error to check
 * @returns true if the error is retryable
 */
function isRetryableError(error: Error): boolean {
  const message = error.message.toLowerCase();

  // Network errors
  if (
    message.includes("network") ||
    message.includes("fetch") ||
    message.includes("failed to fetch") ||
    message.includes("connection") ||
    message.includes("timeout")
  ) {
    return true;
  }

  // Server errors (5xx)
  if (
    message.includes("http 5") ||
    message.includes("500") ||
    message.includes("502") ||
    message.includes("503") ||
    message.includes("504")
  ) {
    return true;
  }

  // Rate limiting
  if (message.includes("429") || message.includes("rate limit")) {
    return true;
  }

  // Not retryable: auth failures (401, 403), validation errors (400), etc.
  return false;
}
